import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import type { ScmProviderDescriptor } from '../../api/types';

const useStyles = makeStyles(theme => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
  silentLabel: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
  },
}));

export interface ProviderSelectorProps {
  providers: ScmProviderDescriptor[];
  value: string | null;
  onChange: (provider: ScmProviderDescriptor) => void;
  disabled?: boolean;
}

/**
 * Renders a provider dropdown only when more than one is configured.
 *
 * - 0 providers: `null` (caller should handle the empty state separately).
 * - 1 provider:  silent informative label (`Azure DevOps`) so the user knows
 *                which SCM is in use, with no extra click needed.
 * - 2+ providers: `Select` dropdown.
 *
 * The picker is intentionally minimalist — the Spassu brand palette
 * (`#FA4616` + neutrals) is preserved by reusing default theme colors. No
 * provider-specific branding is rendered.
 */
export function ProviderSelector({
  providers,
  value,
  onChange,
  disabled,
}: ProviderSelectorProps): JSX.Element | null {
  const classes = useStyles();

  if (providers.length === 0) return null;

  if (providers.length === 1) {
    const only = providers[0];
    return (
      <Typography
        className={classes.silentLabel}
        variant="caption"
        data-testid="provider-selector-silent"
      >
        Provider: {only.displayName}
      </Typography>
    );
  }

  return (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth
      className={classes.formControl}
      disabled={disabled}
    >
      <InputLabel id="provider-selector-label">Provider</InputLabel>
      <Select
        labelId="provider-selector-label"
        label="Provider"
        data-testid="provider-selector"
        value={value ?? ''}
        onChange={ev => {
          const next = providers.find(p => p.id === ev.target.value);
          if (next) onChange(next);
        }}
      >
        {providers.map(p => (
          <MenuItem key={p.id} value={p.id}>
            {p.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
